import FieldsFactory from 'components/form/FieldsFactory'
import {FormActionGroups} from 'components/form/FormActionBar'
import {Formik} from 'formik'
import _ from 'lodash'
import useTranslate from 'modules/local/useTranslate'
import React, {useMemo, useState} from 'react'
import {ErrorSummaries} from 'views/AppLocals/factory/createEditableForm'
import {NullAsDefault, shallowDiff} from 'views/Shared'
import SlateEditor from 'views/SlateEditor'
import {convertFromString, convertToString} from 'views/SlateEditor/functions'
import {ModalEditorToolbar} from 'views/SlateEditor/SlateEditor'
import {createControlledFormField, createNumberControlledInput} from '../../factory/createFormField'
import TextArea from "antd/lib/input/TextArea";
import {ActionModalWrapper} from "../../../../components/form/ActionModalWrapper";
import {
    lengthBetween, noDoubleSpaces,
    noLeadingTrailingSpaces, noNumber,
    noSpecialChars,
    required, runRules
} from "../../../../components/form/ValidateForm";
import dayjs from "dayjs";


export const formSchema = {
    title: {
        title: 'subject name',
        children: [
            {
                name: 'title',
                component: createControlledFormField({
                    placeholder: 'subject name',
                    showCount: true,
                    maxLength: 180,
                }),
            },
        ],
    },
    description: {
        title: 'short description',
        children: [
            {
                name: 'description',
                component: createControlledFormField({
                    placeholder: 'short description',
                    className: 'scrollBarThin',
                    Component: TextArea,
                    showCount: true,
                    maxLength: 500,
                    autoSize: {minRows: 4, maxRows: 6},
                }),
            },
        ],
    },

    number_of_sessions__credits: {
        inline: true,
        className: 'grid grid-cols-1 md:grid-cols-2 gap-3',
        children: [
            {
                label: 'number of sessions',
                name: 'number_of_sessions',
                component: createNumberControlledInput({
                    placeholder: 'ex : 30',
                    inputMode: 'number',
                    min: 1,
                    max: 1000
                }),
            },
            // {
            //     label: 'number of credits',
            //     name: 'number_of_credits',
            //     component: createNumberControlledInput({
            //         placeholder: 'ex : 30',
            //         inputMode: 'number',
            //     }),
            // },
        ],
    },
    // number_of_credits: {
    //     title: 'number of credits',
    //     children: [
    //         {
    //             name: 'number_of_credits',
    //             component: createNumberControlledInput({
    //                 placeholder: 'ex : 30',
    //                 inputMode: 'number',
    //             }),
    //         },
    //     ],
    // },
    about: {
        title: 'about subject',
        children: [
            {
                name: 'about',
                component: createControlledFormField({
                    Component: SlateEditor,
                    ToolbarComponent: ModalEditorToolbar,
                    className: 'background border rounded-lg',
                    withProps: ({value}) => ({
                        defaultValue: value,
                    }),
                    withValues: ({resetKey}) => ({
                        resetKey,
                    }),
                }),
            },
        ],
    },
}

const validateTitle = (name, t) => {
    const rules = [
        required(t),
        lengthBetween(2, 180, t),
        noSpecialChars(t),
        noLeadingTrailingSpaces(t),
        noDoubleSpaces(t),
    ].filter(Boolean)
    const message = runRules(name, rules)
    if (message) return { title: message }
}

const validateNumberOfSessions = (sessions, t) => {
    const rules = [required(t),].filter(Boolean)
    const message = runRules(sessions, rules)
    if (message) return { number_of_sessions: message }
}

const validate = (t) => (values) => {
    const {
        title,
        description,
        number_of_sessions,
        about
    } = values ?? {}
    const errorTitle = validateTitle(title, t)
    const errorNumberOfSessions = validateNumberOfSessions(number_of_sessions, t)
    return {
        ...errorTitle,
        ...errorNumberOfSessions
    }
}

export const CreateSubjectModal = ({
                                       owner_id,
                                       owner_type,
                                       onCancel = NullAsDefault,
                                       onSubmit = NullAsDefault,
                                   }) => {
    const t = useTranslate()
    const initialValues = useMemo(() => {
        return {
            owner_id,
            owner_type,
            title: undefined,
            description: undefined,
            number_of_sessions: 1,
        }
    }, [owner_id, owner_type])

    return (
        <Formik
            validate={validate(t)}
            validateOnMount={true}
            validateOnChange={true}
            initialValues={initialValues}
            onSubmit={(values) => {
                const {about, resetKey, owner_id, owner_type, ...rest} = values ?? {}
                onSubmit(
                    _.omitBy(
                        {
                            ...rest,
                            about: convertToString(about),
                        },
                        _.isUndefined
                    )
                )
            }}
            enableReinitialize={true}>
            <React.Fragment>
                <ActionModalWrapper
                    action={
                        <FormActionGroups
                            submitProps={{
                                label: 'create',
                            }}
                        />
                    }>
                    <FieldsFactory
                        formSchema={[
                            formSchema.title,
                            formSchema.description,
                            formSchema.number_of_sessions__credits,
                            formSchema.about,
                        ]}
                    />
                </ActionModalWrapper>
            </React.Fragment>
        </Formik>
    )
}

export const EditSubjectModal = ({
                                     owner_id,
                                     owner_type,
                                     onCancel = NullAsDefault,
                                     onSubmit = NullAsDefault,
                                     initialValues = {},
                                 }) => {
    const t = useTranslate()

    const [current, setCurrent] = useState('overview')

    const _initialValues = useMemo(() => {
        const {about, ...rest} = initialValues ?? {}
        return {
            ...rest,
            owner_id,
            owner_type,
            about: convertFromString(about),
        }
    }, [initialValues, owner_id, owner_type])

    return (
        <Formik
            validate={validate(t)}
            validateOnMount={true}
            validateOnChange={true}
            enableReinitialize={true}
            initialValues={_initialValues}
            onSubmit={(values) => {
                const changes = shallowDiff(values, _initialValues)
                const {about, owner_id, owner_type, ...rest} = changes ?? {}
                onSubmit(
                    _.omitBy(
                        {
                            ...rest,
                            about: about ? convertToString(about) : undefined,
                        },
                        _.isUndefined
                    )
                )
            }}>
            <React.Fragment>
                <ErrorSummaries/>
                <ActionModalWrapper
                    tab={current}
                    action={
                        <FormActionGroups
                            submitProps={{
                                label: 'save',
                            }}
                        />
                    }
                    onTabChange={(key) => {
                        setCurrent(key)
                    }}
                    tabs={[
                        {
                            key: 'overview',
                            label: 'overview',
                            component: ({child}) => child,
                        },
                        {
                            key: 'about',
                            label: 'about subject',
                            component: () => (
                                <FieldsFactory formSchema={[formSchema.about]}/>
                            ),
                        },
                    ]}>
                    <FieldsFactory
                        formSchema={[
                            formSchema.title,
                            formSchema.description,
                            formSchema.number_of_sessions__credits,
                        ]}
                    />
                </ActionModalWrapper>
            </React.Fragment>
        </Formik>
    )
}
